// Common layout helpers and interactive views.
import React, { useState, useCallback } from "react";
import { Pressable, Role, StyleSheet, View } from "react-native";
import { colorBorderPrimary, colorSurfaceDiscovery, colorSurfacePrimary } from "./color";
import { setTitle } from "platform-specific/url";
import { useTranslation } from "./translation";
import { undefA, undefN, undefR, undefS } from "util/stdtypes";


const MIN_HEIGHT_CLICKZONE = 44;
const MIN_WIDTH_CLICKZONE = 44;

export function Pad({size=20}) {
    return <View style={{height: size, width: size}}/>
}

const undefRole : Role | undefined = undefined;

// Hover events can be tested in demos using the HOVER action
export function HoverView({ style=undefR, ariaLabel=undefS, ariaText=undefS, testID=undefS, role=undefRole, hoverStyle=undefR, pressedStyle=undefR, children, disabled=false, onPress, shrink=false, setHover=(h:boolean)=>{}, setPressed=(p:boolean)=>{}, ...props}) {
    const [localHover, setLocalHover] = useState(false);
    const [localPressed, setLocalPressed] = useState(false);
    const [accessibilityPress, setAccessibilityPress] = useState(false);
    
    const handleLayout = (event) => {
        const { height, width } = event.nativeEvent.layout || {};
        if (height <= MIN_HEIGHT_CLICKZONE || width <= MIN_WIDTH_CLICKZONE) {
            setAccessibilityPress(true);
        }
    };
    const tAriaLabel = useTranslation(ariaLabel);

    if (disabled || !onPress) {
        return <View role={role} aria-disabled aria-label={tAriaLabel ?? ariaText} style={style}>{children}</View>
    }
    return <Pressable
        role={role}
        aria-label={tAriaLabel ?? ariaText}
        testID={testID ?? ariaLabel}
        style={[style, localHover ? hoverStyle : null, localPressed ? pressedStyle : null, shrink ? {flexShrink: 1} : null]}
        onPressIn={() => {setLocalPressed(true); setPressed(true)}}
        onPressOut={() => {setLocalPressed(false); setPressed(false)}}
        onPress={onPress}
        onHoverIn={() => { setLocalHover(true); setHover(true) }}
        onHoverOut={() => { setLocalHover(false); setHover(false) }}
        onLayout={handleLayout}
        {...props}
    >
        {children}
        {(accessibilityPress) && <InvisibleExtendedClickArea />}
    </Pressable>
}

function InvisibleExtendedClickArea() {
    const s = InvisibleExtendedClickAreaStyle;
    return <View aria-hidden style={s.clickArea}  />
}

// HACK: React-native doesn't support non-numeric positions.
const InvisibleExtendedClickAreaStyle = StyleSheet.create({
    clickArea: {
        position: 'absolute',
        left: '50%' as any,
        top: '50%' as any,
        width: MIN_WIDTH_CLICKZONE,
        height: MIN_HEIGHT_CLICKZONE,
        transform: "translate(-50%, -50%)" as any,
        minWidth: '100%' as any,
        minHeight: '100%' as any
    }
});

export function Card({ testID=undefS, children }) {
    const s = CardStyle;
    return <View style={s.card} testID={testID}>
        {children}
    </View>
}

const CardStyle = StyleSheet.create({
    card: {
        paddingBottom: 20,
        backgroundColor: colorSurfacePrimary,
        borderBottomColor: colorBorderPrimary,
        borderBottomWidth: 1
    }
});

export function PadBox({children, horiz=undefN, vert=undefN, top=undefN, bottom=undefN, left=undefN, right=undefN}) {
    return <View style={{paddingHorizontal: horiz, paddingVertical: vert, paddingTop: top, paddingBottom: bottom, paddingLeft: left, paddingRight: right}}>
        {children}
    </View>
}

export function HorizBox({children, gap=undefN, center=false, shrink=false, right=false, spread=false, role=undefA}) {
    return <View role={role} style={{
        flexDirection: 'row', 
        alignItems: center ? 'center' : 'flex-start', 
        justifyContent: spread ? 'space-between' : right ? 'flex-end' : 'flex-start',
        flexShrink: shrink ? 1 : 0,
        gap
    }}>
        {children}
    </View>
}

export function VertBox({children, gap=undefN, wide=false,center=false, role=undefRole}) {
    return <View role={role} style={{
        flexDirection: 'column',
        alignItems: center ? 'center' : wide ? undefined : 'flex-start',
        width: wide ? '100%' : null,
        gap
    }}>
        {children}
    </View>
}


export const LoadingScreenStyle = StyleSheet.create({
    outer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    text: {
        color: '#666'
    }
});

export function Narrow({children, pad=true}) {
    return <View style={{flexDirection: 'row', justifyContent: 'space-around', paddingVertical: pad ? 20 : undefined}}>
        <View style={{maxWidth: 600, flexShrink: 1, flexGrow: 1, marginHorizontal: pad ? 20 : undefined}}>
            {children}
        </View>
    </View>
}

export function Center({children, pad=0}) {
    return <View style={{alignSelf: 'center', alignItems: 'center', margin: pad}}>
        {children}
    </View>
}

export function AdaptiveWidth({children, setNarrow, breakpoint=540, style=undefR}) {
    const handleLayout = useCallback((event) => {
        const { width } = event.nativeEvent.layout;
        setNarrow(width < breakpoint);
    }, [setNarrow, breakpoint]);

    if (React.Children.count(children) === 1 && React.isValidElement(children)) {
        const child = React.Children.only(children) as React.ReactElement;
        return React.cloneElement(child, {
            onLayout: handleLayout,
            style: [child.props.style, style],
        });
    }
    return <View style={style} onLayout={handleLayout}>{children}</View>;
}

export function HeaderBox({children, backgroundColor=colorSurfaceDiscovery, horiz=20, vert=20}) {
    return <View style={{paddingHorizontal:horiz, paddingVertical:vert, backgroundColor}}>{children}</View>
}

export function Separator() {
    const s = SeparatorStyle;
    return <View style={s.separator} />
}
const SeparatorStyle = StyleSheet.create({
    separator: {
        height: 1,
        backgroundColor: colorBorderPrimary,
    }
});

export function WindowTitle({label=undefS, text=undefS, formatParams=undefR}) {
    const tLabel = useTranslation(label, formatParams);
    setTitle(tLabel ?? text);
    return null;
}

// HACK: Bump the key to force a re-render, otherwise the old hover state is preserved 
// when the component is unselected, and potentially moves to longer be under the mouse
// without getting a hover out event
export function HoverSelectBox({selected, testID, onPress, children}) {
    const s = HoverSelectBoxStyle;
    const [key, setKey] = useState(0);
    function onLocalPress() {
        onPress();
        setKey(key+1)
    }
    return <HoverView key={key} testID={testID} onPress={onLocalPress} disabled={!onPress || selected} style={s.outline} hoverStyle={s.hover}>
        {children}
    </HoverView>
}
const HoverSelectBoxStyle = StyleSheet.create({
    hover: {
        borderColor: colorBorderPrimary,
        borderWidth: 1,
        borderRadius: 8,
        padding: 0,
        boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.10)' as any, // FIXME: boxShadow not typed for native
    },
    outline: {
        borderColor: colorBorderPrimary,
        borderWidth: 1,
        borderRadius: 8,
        padding: 0
    }
})

export function ShadowBox({children}) {
    const s = ShadowBoxStyle;
    return <View style={s.shadow}>{children}</View>
}

const ShadowBoxStyle = StyleSheet.create({
    shadow: {
        boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.10)' as any, // FIXME: boxShadow not typed for native
        borderRadius: 12
    }
})

export function FlowBox({children, center=false, rowGap=undefN, columnGap=undefN}) {
    return <View style={{
        flexDirection: 'row', flexWrap: 'wrap', justifyContent: center ? 'center' : 'flex-start',
        rowGap, columnGap
    }}>
        {children}
    </View>
}

export function Container({children}) {
    const s = ContainerStyle;
    return <View style={s.outer}>
        {children}
    </View>
}

const ContainerStyle = StyleSheet.create({
    outer: {
        borderColor: colorBorderPrimary,
        borderWidth: 1,
        borderRadius: 12,
    }
});
